import React, { useState, useEffect, useCallback, useRef } from 'react';
import './FormCanvas.css';
import FormRenderer from './FormRenderer'; // FormRenderer 추가
import { getForms, getCurrentTenant } from '../../services/FormsService';

/**
 * FormCanvas - 동적 슬라이딩 폼 캔버스 컴포넌트
 * ChatGPT 캔버스나 Claude Artifact 스타일의 슬라이딩 UI
 */
const FormCanvas = ({ 
  isOpen = false, 
  onClose, 
  selectedFormId = null, 
  prefilledData = null,
  tenantId = null 
}) => {
  // 상태 관리
  const [forms, setForms] = useState([]);
  const currentTenant = tenantId || getCurrentTenant();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [formData, setFormData] = useState(prefilledData || {}); // FormRenderer에서 사용

  // 포커스를 위한 ref
  const canvasRef = useRef(null);

  // 폼 목록 로드
  const loadForms = async () => {
    if (!currentTenant) {
      setError('테넌트가 선택되지 않았습니다.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getForms(currentTenant);
      setForms(response.forms || []);
    } catch (err) {
      console.error('Error loading forms:', err);
      setError('폼 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 캔버스가 열릴 때 폼 로드
  useEffect(() => {
    if (isOpen && currentTenant) {
      loadForms();
    }
  }, [isOpen, currentTenant]);

  // 선택된 폼 찾기
  const currentForm = forms.find(form => form.form_id === selectedFormId);

  // 미리 채워진 데이터 설정
  useEffect(() => {
    if (prefilledData) {
      setFormData(prefilledData);
    }
  }, [prefilledData]);



  // 오버레이 클릭시 닫기
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  }, [onClose]);

  // ESC 키 눌렀을 때 닫기 (폼이 선택되지 않은 상태에서만)
  useEffect(() => {
    const handleEscKey = (e) => {
      // 폼이 선택된 상태에서는 FormRenderer가 ESC를 처리하도록 함
      if (e.key === 'Escape' && isOpen && !selectedFormId) {
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey, { passive: false }); // preventDefault는 사용하지 않지만 일관성을 위해 false
      // 배경 스크롤 차단 제거 - 자유로운 상호작용을 위해
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose, selectedFormId]);

  // 폼 목록 렌더링
  const renderFormsList = () => {
    if (isLoading) {
      return (
        <div className="form-canvas-loading">
          <div className="spinner"></div>
          <p>폼 목록을 불러오는 중...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="form-canvas-error">
          <p>{error}</p>
          <button 
            onClick={loadForms}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            다시 시도
          </button>
        </div>
      );
    }

    if (forms.length === 0) {
      return (
        <div className="form-canvas-error">
          <p>사용 가능한 폼이 없습니다.</p>
        </div>
      );
    }

    return (
      <div className="form-buttons-grid">
        {forms.map((form) => (
          <button
            key={form.form_id}
            className={`form-button ${selectedFormId === form.form_id ? 'active' : ''}`}
            onClick={() => console.log('Form selected from inside canvas:', form.form_id)}
          >
            <div className="form-button-title">{form.title}</div>
            <div className="form-button-description">{form.description}</div>
          </button>
        ))}
      </div>
    );
  };

  // FormRenderer 성공/에러 핸들러
  const handleFormSuccess = useCallback((result) => {
    console.log('Form submission successful:', result);
  }, []);

  const handleFormError = useCallback((error) => {
    console.error('Form submission error:', error);
  }, []);

  // 메인 콘텐츠 렌더링 결정
  const renderMainContent = () => {
    // 폼이 선택된 경우 FormRenderer 표시
    if (selectedFormId && currentForm) {
      return (
        <div className="form-render-section">
          <FormRenderer
            form={currentForm}
            prefilledData={formData}
            onSuccess={handleFormSuccess}
            onError={handleFormError}
            onClose={onClose}  // 폼 닫기 콜백 전달
          />
        </div>
      );
    }

    // 폼이 선택되지 않은 경우 폼 목록 표시
    return (
      <>
        {/* 폼 목록 섹션 */}
        <div className="form-list-section">
          <h3>사용 가능한 폼</h3>
          {renderFormsList()}
        </div>

        {/* 안내 메시지 */}
        <div className="form-render-section">
          <h3>폼 선택</h3>
          <div style={{ 
            textAlign: 'center', 
            color: '#64748b', 
            padding: '40px 20px',
            fontSize: '0.95rem'
          }}>
            좌측 탭에서 폼을 선택하거나 위에서 폼을 클릭해주세요.
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={`form-canvas-overlay ${isOpen ? 'open' : ''}`} onClick={handleOverlayClick}>
      <div 
        ref={canvasRef}
        className="form-canvas"
        tabIndex={-1}
        style={{ outline: 'none' }}
      >
        {/* 헤더 */}
        <div className="form-canvas-header">
          <h2 className="form-canvas-title">
            {currentForm ? currentForm.title : '폼 입력 시스템'}
            {currentTenant && (
              <span style={{ 
                fontSize: '0.85rem', 
                opacity: '0.8',
                fontWeight: '400'
              }}>
                ({currentTenant})
              </span>
            )}
          </h2>
          <button 
            className="form-canvas-close" 
            onClick={onClose}
            aria-label="폼 캔버스 닫기"
          >
            ✕
          </button>
        </div>

        {/* 콘텐츠 */}
        <div className="form-canvas-content">
          {renderMainContent()}
        </div>
      </div>
    </div>
  );
};

export default FormCanvas; 